import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Facebook, Lock, Menu, Phone, ShoppingBag, X } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { api } from "@/lib/api";

const LINKS = [
  { label: "Donate", id: "donate" },
  { label: "Home", id: "home" },
  { label: "About us", id: "about" },
  { label: "Why us", id: "about" },
  { label: "Services", id: "services" },
  { label: "Events", id: "events" },
  { label: "Purchase Merchandise", id: "shop" },
  { label: "Testimonials", id: "testimonials" },
  { label: "Book a Call", id: "contact" },
];

export const scrollToSection = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: 0, duration: 1.4 });
  else el.scrollIntoView({ behavior: "smooth" });
};

function LogoBlock({ compact = false }) {
  return (
    <button
      data-testid="nav-logo-button"
      onClick={() => scrollToSection("home")}
      className="flex flex-col items-start gap-2 text-left"
    >
      <img src="/assets/logo.png" alt="AB Hunter Consulting USA logo" className={`${compact ? "h-10" : "h-16"} w-auto object-contain`} />
      <span className="font-display text-sm font-semibold italic tracking-wide text-gold">Consulting USA</span>
    </button>
  );
}

function MenuList({ onNavigate, idPrefix = "nav" }) {
  const navigate = useNavigate();
  const [pages, setPages] = useState([]);

  useEffect(() => {
    api.get("/pages").then(({ data }) => setPages(data)).catch(() => {});
  }, []);

  return (
    <nav className="flex flex-col gap-1" aria-label="Primary">
      {LINKS.map((l, i) => (
        <button
          key={`${l.id}-${i}`}
          data-testid={`${idPrefix}-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
          onClick={() => onNavigate(l.id)}
          className="group flex items-center gap-3 rounded-lg px-3 py-2.5 text-left text-[15px] font-medium text-foreground/75 transition-all duration-300 hover:bg-gold/5 hover:text-gold"
        >
          <span className="h-px w-0 bg-gold transition-all duration-300 group-hover:w-5" />
          {l.label}
        </button>
      ))}
      {pages.map((p) => (
        <button
          key={p.id}
          data-testid={`${idPrefix}-page-${p.slug}`}
          onClick={() => { navigate(`/p/${p.slug}`); onNavigate("__none__"); }}
          className="group flex items-center gap-3 rounded-lg px-3 py-2.5 text-left text-[15px] font-medium text-foreground/75 transition-all duration-300 hover:bg-gold/5 hover:text-gold"
        >
          <span className="h-px w-0 bg-gold transition-all duration-300 group-hover:w-5" />
          {p.title}
        </button>
      ))}
      <button
        data-testid={`${idPrefix}-link-blog`}
        onClick={() => { navigate("/blog"); onNavigate("__none__"); }}
        className="group flex items-center gap-3 rounded-lg px-3 py-2.5 text-left text-[15px] font-medium text-foreground/75 transition-all duration-300 hover:bg-gold/5 hover:text-gold"
      >
        <span className="h-px w-0 bg-gold transition-all duration-300 group-hover:w-5" />
        Blog
      </button>
    </nav>
  );
}

function CartRow() {
  const { count, setOpen } = useCart();
  return (
    <button
      data-testid="sidebar-cart-button"
      onClick={() => setOpen(true)}
      className="mt-6 flex w-full items-center justify-between rounded-xl border border-gold/30 bg-gold/5 px-4 py-3 text-sm font-semibold text-gold transition-all duration-300 hover:bg-gold/15"
    >
      <span className="flex items-center gap-2.5"><ShoppingBag className="h-4 w-4" /> Your Cart</span>
      <span data-testid="cart-count-badge" className="flex h-6 min-w-6 items-center justify-center rounded-full bg-gold px-1.5 font-mono text-[11px] font-bold text-ink">
        {count}
      </span>
    </button>
  );
}

function CartIconButton() {
  const { count, setOpen } = useCart();
  return (
    <button
      data-testid="mobile-cart-button"
      aria-label="Open cart"
      onClick={() => setOpen(true)}
      className="relative flex h-10 w-10 items-center justify-center rounded-full border border-gold/30 text-gold"
    >
      <ShoppingBag className="h-4 w-4" />
      {count > 0 && (
        <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-gold px-1 font-mono text-[10px] font-bold text-ink">
          {count}
        </span>
      )}
    </button>
  );
}

function SideFooter() {
  return (
    <div className="mt-auto space-y-4 pt-8">
      <div className="flex items-center justify-between px-3">
        <a href="tel:2256034116" data-testid="nav-phone-link" className="flex items-center gap-2.5 text-sm text-muted-foreground transition-colors hover:text-gold">
          <Phone className="h-3.5 w-3.5 text-gold" /> 225.603.4116
        </a>
        <a href="https://www.facebook.com/abhunterconsultingusa/" target="_blank" rel="noopener noreferrer" data-testid="nav-facebook-link" aria-label="AB Hunter on Facebook"
           className="flex h-8 w-8 items-center justify-center rounded-full border border-gold/25 text-gold/70 transition-all hover:border-gold hover:text-gold">
          <Facebook className="h-3.5 w-3.5" />
        </a>
      </div>
      <a href="/admin/login" data-testid="nav-admin-link" className="flex items-center gap-2.5 px-3 text-sm text-muted-foreground transition-colors hover:text-gold">
        <Lock className="h-3.5 w-3.5 text-gold" /> Admin Login
      </a>
      <p className="px-3 font-mono text-[9px] uppercase tracking-[0.25em] text-muted-foreground/50">Building Bridges · Elevating Communities · Transforming Leaders</p>
    </div>
  );
}

export default function SiteNav() {
  const [open, setOpen] = useState(false);
  const go = useCallback((id) => { setOpen(false); scrollToSection(id); }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <>
      {/* Desktop sidebar */}
      <aside
        data-testid="site-sidebar"
        className="fixed inset-y-0 left-0 z-50 hidden w-64 flex-col border-r border-gold/15 bg-ink/95 px-5 py-7 backdrop-blur-xl lg:flex"
      >
        <LogoBlock />
        <CartRow />
        <div className="mt-8">
          <MenuList onNavigate={go} />
        </div>
        <SideFooter />
      </aside>

      {/* Mobile top bar with hamburger */}
      <div className="fixed inset-x-0 top-0 z-50 flex items-center justify-between border-b border-gold/15 bg-ink/90 px-4 py-3 backdrop-blur-xl lg:hidden">
        <button
          data-testid="mobile-menu-button"
          aria-label="Open menu"
          onClick={() => setOpen(true)}
          className="flex h-10 w-10 items-center justify-center rounded-full border border-gold/30 text-gold"
        >
          <Menu className="h-5 w-5" />
        </button>
        <button data-testid="mobile-logo-button" onClick={() => go("home")} aria-label="Home">
          <img src="/assets/logo.png" alt="AB Hunter Consulting USA logo" className="h-9 w-auto object-contain" />
        </button>
        <div className="flex items-center gap-2">
          <CartIconButton />
          <a href="/admin/login" data-testid="mobile-admin-link" aria-label="Admin login"
             className="flex h-10 w-10 items-center justify-center rounded-full border border-gold/30 text-gold/80">
            <Lock className="h-4 w-4" />
          </a>
        </div>
      </div>

      {/* Mobile slide-in menu */}
      <AnimatePresence>
        {open && (
          <>
            <motion.div
              data-testid="mobile-menu-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              onClick={() => setOpen(false)}
              className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm lg:hidden"
            />
            <motion.aside
              data-testid="mobile-menu-panel"
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
              className="fixed inset-y-0 left-0 z-[70] flex w-72 flex-col border-r border-gold/20 bg-ink px-5 py-6 lg:hidden"
            >
              <div className="flex items-start justify-between">
                <LogoBlock compact />
                <button
                  data-testid="mobile-menu-close-button"
                  aria-label="Close menu"
                  onClick={() => setOpen(false)}
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-gold/30 text-gold"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              <CartRow />
              <div className="mt-6">
                <MenuList onNavigate={go} idPrefix="mobile-nav" />
              </div>
              <SideFooter />
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
